import { motion } from "framer-motion";
import { MapPin, Clock } from "lucide-react";
import { RECEPTION_EVENT, COUPLE } from "@/data/wedding";
import { GoldDivider } from "./Ornaments";
import { WEDDING } from "@/constants/testIds";

const marqueeItems = [
  "Chithra & Vishnu",
  "13 · 09 · 2026",
  "Ramapuram",
  "ചിങ്ങത്തിൽ താലികെട്ട്",
  "With Love & Blessings",
];

export const Reception = () => {
  return (
    <section id="reception" className="relative overflow-hidden bg-[var(--green-deep)] text-[var(--kasavu)]">
      {/* subtle depth wash */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(76,124,62,0.35),transparent_60%)]" />

      {/* editorial marquee */}
      <div className="relative overflow-hidden border-y border-gold/30 py-4">
        <motion.div
          className="flex whitespace-nowrap"
          animate={{ x: ["0%", "-50%"] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
        >
          {[...marqueeItems, ...marqueeItems].map((m, i) => (
            <span
              key={i}
              className="mx-8 font-display text-2xl italic text-[var(--gold)] md:text-3xl"
            >
              {m} <span className="mx-6 text-kasavu/40">✦</span>
            </span>
          ))}
        </motion.div>
      </div>

      <div className="relative px-6 py-24 md:py-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mx-auto max-w-3xl text-center"
        >
          <p className="font-body text-[11px] uppercase tracking-mega text-[var(--gold)]">
            Chapter II —{" "}
            <span className="font-malayalam normal-case tracking-normal">
              {RECEPTION_EVENT.malayalam}
            </span>
          </p>
          <h2 className="mt-4 font-display text-5xl italic md:text-7xl">
            {RECEPTION_EVENT.title}
          </h2>
          <p className="mx-auto mt-4 max-w-md font-body text-sm leading-relaxed text-kasavu/70">
            Join us for an evening of celebration, music and blessings as we
            begin our new life together.
          </p>
        </motion.div>

        {/* time + venue placards */}
        <div className="mx-auto mt-14 grid max-w-4xl gap-6 md:grid-cols-2">
          <Placard icon={Clock} title="When">
            <p className="font-display text-2xl italic">{RECEPTION_EVENT.date}</p>
            <p className="mt-1 font-body text-sm text-kasavu/75">
              {RECEPTION_EVENT.time}
            </p>
          </Placard>
          <Placard icon={MapPin} title="Where">
            <p className="font-display text-2xl italic">{RECEPTION_EVENT.venue}</p>
            <p className="mt-1 font-body text-sm text-kasavu/75">
              {RECEPTION_EVENT.address}
            </p>
            <a
              data-testid={WEDDING.mapLinkReception}
              href={RECEPTION_EVENT.mapUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-[var(--gold)] px-5 py-2 font-body text-[11px] uppercase tracking-[0.2em] text-[var(--gold)] transition-colors duration-300 hover:bg-[var(--gold)] hover:text-[var(--green-deep)]"
            >
              <MapPin className="h-4 w-4" strokeWidth={1.6} /> Open location
            </a>
          </Placard>
        </div>

        <GoldDivider className="mt-16 opacity-70" />

        <p className="mx-auto mt-8 max-w-lg text-center font-script text-3xl text-[var(--gold)] md:text-4xl">
          {COUPLE.bride.name} &amp; {COUPLE.groom.name}
        </p>
      </div>
    </section>
  );
};

const Placard = ({ icon: Icon, title, children }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7 }}
    className="rounded-3xl border border-gold/40 bg-white/[0.06] p-8 backdrop-blur-md"
  >
    <div className="flex items-center gap-2 text-[var(--gold)]">
      <Icon className="h-5 w-5" strokeWidth={1.5} />
      <span className="font-body text-[10px] uppercase tracking-[0.28em]">{title}</span>
    </div>
    <div className="mt-4">{children}</div>
  </motion.div>
);
